import * as R from 'ramda';
import _ from 'lodash';
import React from 'react';
import fp from 'lodash/fp';

import Icons, { Size } from '../Common/Icons';
import { NodeRelationshipType, NodeType } from '../../utils/types';
import { sortLocale } from '../../utils/string';


export const iconMap = {
  [NodeType.Node]: <Icons.ParentChild size={Size.SMALL} />,
  [NodeType.Dashboard]: <Icons.Dashboard size={Size.SMALL} />,
  [NodeType.Asset]: <Icons.Asset size={Size.SMALL} />
};


export const updateExpandOnItems = R.curry(function updateExpandOnItems(expandNodes, node) {
  let defaultExpanded = false;
  if (expandNodes[0] === node.id) {
    expandNodes.shift();
    defaultExpanded = true;
  }
  return {
    ...node,
    defaultExpanded: defaultExpanded
  };
});


export const createDataItem = R.curry(function createDataItem(node) {
  return {
    ...node,
    icon: iconMap[node.type],
    id: node.id,
    name: node.displayName ?? node.name ?? node.id
  };
});

export const selectItem = R.curry(function selectItem(selectedItems, node) {
  const checked = _.some(selectedItems, item => item?.id === node?.id && item?.type === node?.type);
  if (node.checkProps == null) {
    node.checkProps = { checked };
  } else {
    node.checkProps.checked = checked;
  }
  return node;
});

export const pruneEmptyChildren = R.curry(function (node) {
  if (_.isEmpty(node.children)) {
    return { ...node, children: undefined };
  }
  return node;

});

export const copyDashFn = R.curry(function (node) {
  let c;
  if (!_.isEmpty(node.children)) {
    c = { ...node, dashboards: R.filter((c) => c.type === NodeType.Dashboard, node.children) };
  } else {
    c = node;
  }
  return c;
});

export const addDescFn = R.curry(function (node) {
  return node;
});

export const matchDescFn = R.curry(function (nodes) {
  return nodes;
});

export const addParent = R.curry(function (parent, node) {
  return { ...node, parent: parent };
});

export const filterTreeNodeTypes = R.curry(function filterTreeNodeTypes(nodeTypes, node) {
  if (nodeTypes.includes(node.type)) {
    return node;
  }
});

export const filterTreeRelationship = R.curry(function (relationship, node) {
  if (relationship.includes(node?.edge?.relation)) {
    return node;
  }
});


export const NODE_FILTER = [NodeType.Node, NodeType.Asset];

const mapNodeInTree = R.curry(function mapNode(mapper, node) {
  mapper(node);
  if (node.children == null) {
    return node;
  }
  _.forEach(node?.children, mapNodeInTree(mapper));
  return node;
});

export const buildTree = (tree, selectedItems = null) => {
  let relationship = [NodeRelationshipType.Contains, NodeRelationshipType.DependsOn];
  if (tree?.pruneAssetDependencies === true) {
    relationship = [NodeRelationshipType.Contains];
  }
  const nodeMap = {};
  const result = fp.compose(
    mapNodeInTree((node) => {
      nodeMap[node._key] = node;
    }),
    TreeFuncs.map(createDataItem()),
    TreeFuncs.map(selectItem(selectedItems)),
    TreeFuncs.map(pruneEmptyChildren()),
    TreeFuncs.mapDesc(matchDescFn, addDescFn),
    TreeFuncs.filter(filterTreeRelationship([...relationship, null, undefined])),
    TreeFuncs.filter(filterTreeNodeTypes(NODE_FILTER)),
    TreeFuncs.map(copyDashFn),
    TreeFuncs.mapWithParent(addParent, null)
  )(tree);
  result.nodeMap = nodeMap;
  return result;
};


// https://jrsinclair.com/articles/2019/functional-js-traversing-trees-with-recursive-reduce/
function hasChildren(node) {
  return (typeof node === 'object')
        && (typeof node.children !== 'undefined')
        && (node.children.length > 0);
}

const TreeFuncs = {
  reduce: R.curry(function reduce(reducerFn, init, node) {
    const acc = reducerFn(init, node);
    if (!hasChildren(node)) {
      return acc;
    }
    return node.children.reduce(TreeFuncs.reduce(reducerFn), acc);
  }),
  map: R.curry(function map(mapFn, node) {
    // console.log('map', node);
    const newNode = mapFn(node);
    if (!hasChildren(node)) {
      return newNode;
    }
    newNode.children = newNode.children.map(TreeFuncs.map(mapFn));
    return newNode;
  }),
  mapDesc: R.curry(function map(matchDescFn, mapFn, node) {
    // console.log('mapDesc', node);
    const newNode = mapFn(node);
    newNode.assetDescendants = matchDescFn(node?.children) || [];
    if (!hasChildren(node)) {
      return newNode;
    }
    const l = newNode.children.map(TreeFuncs.mapDesc(matchDescFn, mapFn));
    // newNode.assetDescendants = flatten([...newNode.children.map(TreeFuncs.mapDesc(matchDescFn, mapFn))]);
    newNode.assetDescendants = R.flatten([...newNode.assetDescendants, ...l.map((n) => n.assetDescendants)]);
    return newNode;
  }),
  mapWithParent: R.curry(function (mapFn, parent, node) {
    // console.log('mapWithParent', node);
    const newNode = mapFn(parent, node);
    if (!hasChildren(node)) {
      return newNode;
    }
    const children = sortLocale(node.children, 'name');
    newNode.children = children.map(TreeFuncs.mapWithParent(mapFn, newNode));
    return newNode;
  }),
  findOne: R.curry(function findOne(findFn, node) {
    const newNode = findFn(node);
    if (newNode || !hasChildren(node)) {
      return newNode;
    }
    return _.compact(R.flatten(node.children.map(TreeFuncs.findOne(findFn))))[0];
  }),
  filter: R.curry(function filter(filterFn, node) {
    const newNode = filterFn(node);
    if (!hasChildren(node) || newNode === undefined) {
      return newNode;
    }
    newNode.children = node.children.filter(TreeFuncs.filter(filterFn));
    return newNode;
  }),
  filterList: R.curry(function filterList(filterFn, node) {
    const newNode = filterFn(node);
    if (!hasChildren(node)) {
      return newNode;
    }
    if (newNode === undefined) {
      return {};
    }
    newNode.children = node.children.filter(TreeFuncs.filterList(filterFn));
    return newNode;
  }),
  del: R.curry(function del(deleteFn, node) {
    const newNode = deleteFn(node);
    if (!hasChildren(node) || newNode === undefined) {
      return newNode;
    }
    newNode.children = node.children.map(TreeFuncs.del(deleteFn)).filter(x => x !== undefined);

    return newNode;
  })
};


export default TreeFuncs;

// function flattenToArray(arr, {children, ...data}) {
//     return arr.concat([{...data}]);
// }


// function addChildCount(node) {
//     const countstr = (hasChildren(node)) ? ` (${node.children.length})` : '';
//     return {
//       ...node,
//       text: node.text + countstr,
//     }
//   }


// const prependHost = curry(function prependHost(host, node) {
//     if (node.nodeType !== 'link') { return node; }
//     return {
//       ...node,
//       href: node.href.replace(/^\//, host),
//     }
//   });

